import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useContent } from "../context/ContentContext";
import { Pencil } from "lucide-react";

/**
 * Inline-editable text. Non-admins see static text. Admins see a hover affordance
 * and can click to edit. Debounced save happens via ContentContext.
 */
export default function EditableText({
  path,
  value,
  as: Tag = "span",
  multiline = false,
  className = "",
  placeholder = "Click to edit",
  testId,
  preserveWhitespace = false,
}) {
  const { isAdmin } = useAuth();
  const { updateField } = useContent();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const ref = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(value ?? "");
  }, [value, editing]);

  useEffect(() => {
    if (editing && ref.current) {
      ref.current.focus();
      const el = ref.current;
      if (el.setSelectionRange) {
        const len = el.value.length;
        try { el.setSelectionRange(len, len); } catch (_) {}
      }
      if (multiline) autoresize(el);
    }
  }, [editing, multiline]);

  const commit = () => {
    if (draft !== value) updateField(path, draft);
    setEditing(false);
  };

  const cancel = () => {
    setDraft(value ?? "");
    setEditing(false);
  };

  const onKey = (e) => {
    if (!multiline && e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    } else if (multiline && e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      commit();
    }
  };

  if (!isAdmin) {
    return (
      <Tag
        className={className}
        style={preserveWhitespace ? { whiteSpace: "pre-wrap" } : undefined}
        data-testid={testId}
      >
        {value}
      </Tag>
    );
  }

  return (
    <Tag
      className={`editable-field admin-mode ${editing ? "editing" : ""} ${className}`}
      data-testid={testId}
      style={preserveWhitespace ? { whiteSpace: "pre-wrap" } : undefined}
      onClick={(e) => {
        if (!editing) {
          e.stopPropagation();
          setEditing(true);
        }
      }}
    >
      {editing ? (
        multiline ? (
          <textarea
            ref={ref}
            className="editable-textarea"
            rows={1}
            value={draft}
            placeholder={placeholder}
            onChange={(e) => {
              setDraft(e.target.value);
              autoresize(e.target);
            }}
            onBlur={commit}
            onKeyDown={onKey}
            data-testid={testId ? `${testId}-input` : undefined}
          />
        ) : (
          <input
            ref={ref}
            className="editable-input"
            value={draft}
            placeholder={placeholder}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={onKey}
            data-testid={testId ? `${testId}-input` : undefined}
          />
        )
      ) : (
        <>
          {value || <span className="opacity-50">{placeholder}</span>}
          <Pencil
            className="inline-block ml-2 opacity-0 group-hover:opacity-60 transition-opacity"
            size={12}
            strokeWidth={1.5}
            aria-hidden
          />
        </>
      )}
    </Tag>
  );
}

function autoresize(el) {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = el.scrollHeight + "px";
}
